import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { useSubmitReview, ReviewPayload } from "../hooks/useSubmitReview";
import { isClientLoggedIn, getStoredClientAuth } from "../../client/ClientAuthContext";
import { useBookingDraft } from "../BookingDraftContext";
import { useTranslation } from "react-i18next";

type FormStatus = "idle" | "sending" | "success" | "error";

interface ReviewSubmitFormProps {
    staffId: number;
    onCancel: () => void;
    onSuccess?: () => void;
}

export default function ReviewSubmitForm({
    staffId,
    onCancel,
    onSuccess,
}: ReviewSubmitFormProps) {
    const { t } = useTranslation();
    // Use standalone functions that work outside ClientAuthProvider
    const auth = getStoredClientAuth();
    const isLoggedIn = isClientLoggedIn();

    const { draft } = useBookingDraft();
    const companyId = draft.companyId ?? 0;
    const queryClient = useQueryClient();

    // Form state
    const [rating, setRating] = React.useState(0);
    const [hoverRating, setHoverRating] = React.useState(0);
    const [reviewText, setReviewText] = React.useState("");
    const [clientName, setClientName] = React.useState(auth?.name ?? "");
    const [clientPhone, setClientPhone] = React.useState(auth?.phone ?? "");
    const [clientEmail, setClientEmail] = React.useState("");
    const [status, setStatus] = React.useState<FormStatus>("idle");
    const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

    const mutation = useSubmitReview();

    const handleSubmit = async () => {
        // Validation
        if (rating === 0) {
            setErrorMsg(t("widget.reviews.validation.rating", "Please choose a rating"));
            return;
        }
        if (!reviewText.trim()) {
            setErrorMsg(t("widget.reviews.validation.review", "Please write a review"));
            return;
        }
        if (!clientPhone.trim()) {
            setErrorMsg(t("widget.reviews.validation.phone_required", "Phone is required"));
            return;
        }
        if (!clientPhone.trim().startsWith("+")) {
            setErrorMsg(t("widget.common.validation.phone_country_code_short", "Include the country code (for example, +375...)"));
            return;
        }

        setStatus("sending");
        setErrorMsg(null);

        const payload: ReviewPayload = {
            company_id: companyId,
            staff_id: staffId,
            review: reviewText.trim(),
            rating,
            client_name: clientName.trim() || t("widget.common.guest", "Guest"),
            client_phone: clientPhone.trim(),
        };

        // Logged-in: include client_id
        if (isLoggedIn && auth) {
            payload.client_id = auth.clientId;
        } else if (clientEmail.trim()) {
            // Guest flow: include email if provided
            payload.client_email = clientEmail.trim();
        }

        try {
            await mutation.mutateAsync(payload);
            setStatus("success");
            // Invalidate availability queries to refetch reviews
            queryClient.invalidateQueries({ queryKey: ["staffAvailabilityRange"] });
            onSuccess?.();
        } catch (e: any) {
            setStatus("error");
            setErrorMsg(e?.response?.data?.detail || e?.message || t("widget.common.submit_error", "Could not submit"));
        }
    };

    if (status === "success") {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onCancel}>
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                <div
                    className="relative z-10 bg-card rounded-2xl shadow-2xl p-6 flex flex-col items-center gap-4 animate-in fade-in zoom-in-95 duration-200"
                    onClick={(e) => e.stopPropagation()}
                >
                    <CheckCircle2 className="w-12 h-12 text-green-500" />
                    <p className="text-base font-medium text-foreground text-center">
                        {t("widget.reviews.success", "Thank you for your review!")}
                    </p>
                    <Button
                        variant="outline"
                        className="mt-2 rounded-xl"
                        onClick={onCancel}
                    >
                        {t("widget.common.close", "Close")}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 py-4 border-t border-b">
            {/* Star Rating */}
            <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">{t("widget.reviews.rating", "Rating")}</Label>
                <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            type="button"
                            className="p-1 transition-transform hover:scale-110"
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            onClick={() => setRating(star)}
                        >
                            <Star
                                className={`w-7 h-7 transition-colors ${star <= (hoverRating || rating)
                                    ? "fill-yellow-400 text-yellow-400"
                                    : "text-muted-foreground/50 stroke-[1.5]"
                                    }`}
                            />
                        </button>
                    ))}
                </div>
            </div>

            {/* Review Text */}
            <div className="space-y-2">
                <Label htmlFor="review-text" className="text-sm font-medium text-muted-foreground">
                    {t("widget.reviews.your_review", "Your review")}
                </Label>
                <Textarea
                    id="review-text"
                    placeholder={t("widget.reviews.placeholder", "Tell us about your experience...")}
                    value={reviewText}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReviewText(e.target.value)}
                    className="min-h-[80px] resize-none"
                    disabled={status === "sending"}
                />
            </div>

            {/* Client Name */}
            <div className="space-y-2">
                <Label htmlFor="client-name" className="text-sm font-medium text-muted-foreground">
                    {t("widget.common.name", "Name")}
                </Label>
                <Input
                    id="client-name"
                    type="text"
                    placeholder={t("widget.common.name_placeholder", "Your name")}
                    value={clientName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientName(e.target.value)}
                    disabled={status === "sending"}
                />
            </div>

            {/* Client Phone */}
            <div className="space-y-2">
                <Label htmlFor="client-phone" className="text-sm font-medium text-muted-foreground">
                    {t("widget.common.phone", "Phone")} <span className="text-red-500">*</span>
                </Label>
                <Input
                    id="client-phone"
                    type="tel"
                    placeholder="+375..."
                    value={clientPhone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientPhone(e.target.value)}
                    disabled={status === "sending"}
                />
            </div>

            {/* Email (guest only) */}
            {!isLoggedIn && (
                <div className="space-y-2">
                    <Label htmlFor="client-email" className="text-sm font-medium text-muted-foreground">
                        {t("widget.common.email_optional", "Email (optional)")}
                    </Label>
                    <Input
                        id="client-email"
                        type="email"
                        placeholder="email@example.com"
                        value={clientEmail}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientEmail(e.target.value)}
                        disabled={status === "sending"}
                    />
                </div>
            )}

            {/* Error Message */}
            {errorMsg && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    {errorMsg}
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
                <Button
                    variant="outline"
                    className="flex-1 h-10 rounded-xl"
                    onClick={onCancel}
                    disabled={status === "sending"}
                >
                    {t("widget.common.cancel", "Cancel")}
                </Button>
                <Button
                    className="flex-1 h-10 rounded-xl font-semibold"
                    onClick={handleSubmit}
                    disabled={status === "sending" || rating === 0}
                >
                    {status === "sending" ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t("widget.common.submitting", "Submitting...")}
                        </>
                    ) : (
                        t("widget.common.submit", "Submit")
                    )}
                </Button>
            </div>
        </div>
    );
}
